/** @jsxImportSource @emotion/react */
import css, { SerializedStyles } from '@emotion/css/macro'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ProvinceSelectInput, Province } from '../ProvinceSelectInput'
import { useCountries, Country } from '@sportninja/common/hooks/useCountries'
import { CountrySelectInput } from '../CountrySelectInput'

/**
 * CountryAndProvinceInput component that displays country and province/state selectors side by side
 * Automatically handles province fetching based on selected country and switches to text input when no provinces exist
 * Works with both iso_3166_2 codes and country names
 * @component
 */
type CountryAndProvinceInputProps = {
  /** Current country value (iso_3166_2 code or country name, depending on valueMode) */
  countryValue: string | undefined
  /** Current province value (id, name, or text) */
  provinceValue: string | undefined
  /** Function to update country - receives value and country ID */
  onCountryChange: (
    value: string | undefined,
    countryId: string | number | undefined
  ) => void
  /** Function to update province - receives value and province ID */
  onProvinceChange: (
    value: string | undefined,
    provinceId: string | number | undefined
  ) => void
  /** Value mode - 'iso_code' uses iso_3166_2, 'name' uses country name, 'id' uses numeric ID (default: 'iso_code') */
  valueMode?: 'iso_code' | 'name' | 'id'
  /** Province value mode - 'id' uses numeric ID, 'name' uses province name (default: 'id') */
  provinceValueMode?: 'id' | 'name' | 'iso_code'
  /** Field errors object */
  fieldErrors?: {
    country?: boolean | string
    province?: boolean | string
    state?: boolean | string
  }
  /** Custom error messages */
  customErrors?: {
    country?: string
    province?: string
  }
  /** Required fields configuration */
  requiredFields?: {
    country?: boolean
    province?: boolean
  }
  /** Labels for the fields */
  labels?: {
    country?: string
    province?: string
  }
  /** Placeholder texts */
  placeholders?: {
    country?: string
    province?: string
  }
  /** Whether to disable the inputs */
  disabled?: boolean
  /** Custom styling for the container */
  containerStyle?: SerializedStyles
  /** Responsive layout flag - if true, stacks vertically on mobile */
  responsive?: boolean
  /** Tab index for country field */
  countryTabIndex?: number
  /** Tab index for province field */
  provinceTabIndex?: number
  /** Whether to allow clearing selections */
  allowClear?: boolean
  /** Custom list of countries to use instead of fetching */
  customCountryList?: Country[]
  /** Ref to provinces to be used to store fetched provinces */
  provincesRef?: React.MutableRefObject<Province[]>
  /** Ref to countries to be used to store fetched countries */
  countriesRef?: React.MutableRefObject<Country[]>
  /** Initial country ID to use before countries are loaded (useful for edit mode) */
  initialCountryId?: string | number | null
  /** Initial province ID to help with value matching before provinces load */
  initialProvinceId?: string | number | null
  /** Callback when provinces are loaded */
  onProvincesLoaded?: (provinces: Province[]) => void
}

export const CountryAndProvinceInput: React.FC<
  CountryAndProvinceInputProps
> = ({
  countryValue,
  provinceValue,
  onCountryChange,
  onProvinceChange,
  valueMode = 'iso_code',
  provinceValueMode = 'name',
  fieldErrors = {},
  customErrors = {},
  labels = {},
  placeholders = {},
  disabled = false,
  containerStyle,
  responsive = false,
  countryTabIndex,
  provinceTabIndex,
  allowClear = false,
  customCountryList,
  requiredFields = {},
  provincesRef,
  countriesRef,
  initialCountryId,

  initialProvinceId: _initialProvinceId,
  onProvincesLoaded,
}) => {
  const { countries: fetchedCountries, refetch } = useCountries()
  const countries = customCountryList || fetchedCountries

  // Sort countries: US first, then Canada, then rest alphabetically by name
  const sortedCountries = useMemo(() => {
    if (!countries.length) {
      return countries
    }

    return [...countries].sort((a, b) => {
      // US always first
      if (a.iso_3166_2 === 'US') {
        return -1
      }
      if (b.iso_3166_2 === 'US') {
        return 1
      }
      // Canada second
      if (a.iso_3166_2 === 'CA') {
        return -1
      }
      if (b.iso_3166_2 === 'CA') {
        return 1
      }
      // Rest alphabetically by name
      return a.name.localeCompare(b.name)
    })
  }, [countries])

  // Track fetched provinces for value conversion
  const [provinces, setProvinces] = useState<Province[]>([])

  // Memoized callback to prevent infinite loop in ProvinceSelectInput
  const handleProvincesLoadedInternal = useCallback(
    (loadedProvinces: Province[]) => {
      setProvinces(loadedProvinces)
      onProvincesLoaded?.(loadedProvinces)
    },
    [onProvincesLoaded]
  )

  // Fetch countries on mount if not already loaded and not using custom list
  useEffect(() => {
    if (!customCountryList && countries.length === 0) {
      refetch()
    }
  }, [customCountryList, countries.length])

  useEffect(() => {
    if (
      countriesRef &&
      sortedCountries.length > 0 &&
      countriesRef.current.length === 0
    ) {
      countriesRef.current = sortedCountries
    }
  }, [sortedCountries, countriesRef])

  // Map country value to numeric ID for province fetching
  const derivedCountryId = useMemo(() => {
    if (!countryValue || !sortedCountries.length) {
      return undefined
    }

    let country: Country | undefined

    if (valueMode === 'id') {
      // Value is already an ID string
      return countryValue
    } else if (valueMode === 'iso_code') {
      // Value is iso_3166_2 code
      country = sortedCountries.find((c) => c.iso_3166_2 === countryValue)
    } else {
      // Value is country name
      country = sortedCountries.find(
        (c) =>
          c.name.toLowerCase() === countryValue.toLowerCase() ||
          c.name_full.toLowerCase() === countryValue.toLowerCase()
      )
    }

    if (country) {
      return country.id
    }

    return undefined
  }, [countryValue, sortedCountries, valueMode])

  // Use initialCountryId as fallback when derived ID is undefined (useful for edit mode)
  const countryId = derivedCountryId ?? initialCountryId ?? undefined

  // Convert between value modes for CountrySelectInput
  const countryValueForInput = useMemo(() => {
    if (!countryValue || !sortedCountries.length) {
      return undefined
    }

    if (valueMode === 'iso_code') {
      // Input expects iso_3166_2, and that's what we have
      return countryValue
    } else if (valueMode === 'id') {
      // Value is ID, need to convert to iso_3166_2 for the input
      const country = sortedCountries.find(
        (c) => String(c.id) === String(countryValue)
      )
      return country?.iso_3166_2
    } else {
      // Value is name, need to convert to iso_3166_2 for the input
      const country = sortedCountries.find(
        (c) =>
          c.name.toLowerCase() === countryValue.toLowerCase() ||
          c.name_full.toLowerCase() === countryValue.toLowerCase()
      )
      return country?.iso_3166_2
    }
  }, [countryValue, sortedCountries, valueMode])

  // Convert province value based on provinceValueMode
  const provinceValueForInput = useMemo(() => {
    if (!provinceValue || !provinces.length) {
      // If no provinces loaded, pass value as-is (for text input mode)
      return provinceValue
    }

    let province: Province | undefined

    if (provinceValueMode === 'id') {
      // Value is already an ID, pass as-is
      return provinceValue
    } else if (provinceValueMode === 'name') {
      // Value is a name, find the ID
      province = provinces.find(
        (p) => p.name.toLowerCase() === provinceValue.toLowerCase()
      )
    } else if (provinceValueMode === 'iso_code') {
      // Value is iso_3166_2 code, find the ID
      province = provinces.find((p) => p.iso_3166_2 === provinceValue)
    }

    if (province) {
      return province.id.toString()
    }

    return undefined
  }, [provinceValue, provinces, provinceValueMode])

  // Track previous countryId to detect actual changes
  const prevCountryIdRef = useRef(countryId)

  // Clear province when country changes
  useEffect(() => {
    // Only clear if countryId actually changed (not on initial mount or province updates)
    if (
      prevCountryIdRef.current !== undefined &&
      prevCountryIdRef.current !== countryId
    ) {
      onProvinceChange(undefined, undefined)
    }
    prevCountryIdRef.current = countryId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId])

  const handleCountryChange = (isoCode: string | undefined) => {
    if (!isoCode) {
      onCountryChange(undefined, undefined)
      return
    }

    // Find the country to get its ID
    const country = sortedCountries.find((c) => c.iso_3166_2 === isoCode)
    const numericId = country?.id

    if (valueMode === 'iso_code') {
      // Pass iso code directly along with numeric ID
      onCountryChange(isoCode, numericId)
    } else if (valueMode === 'id') {
      // Convert iso code to numeric ID
      if (country) {
        onCountryChange(country.id.toString(), numericId)
      }
    } else {
      // Convert iso code to country name
      if (country) {
        onCountryChange(country.name, numericId)
      }
    }
  }

  const handleProvinceChange = (value: string | undefined) => {
    // If no value, clear both
    if (!value) {
      onProvinceChange(undefined, undefined)
      return
    }

    // If no provinces (text input mode), pass value as-is with no ID
    if (!provinces.length) {
      onProvinceChange(value, undefined)
      return
    }

    // Find the province to get its numeric ID
    const province = provinces.find((p) => p.id.toString() === value)
    const numericId = province?.id

    // Convert from ID back to the desired format
    if (provinceValueMode === 'id') {
      // Pass ID as-is along with numeric ID
      onProvinceChange(value, numericId)
    } else {
      // Find the province by ID and convert to name or iso_code
      if (province) {
        if (provinceValueMode === 'name') {
          onProvinceChange(province.name, numericId)
        } else if (provinceValueMode === 'iso_code') {
          onProvinceChange(province.iso_3166_2, numericId)
        }
      } else {
        onProvinceChange(undefined, undefined)
      }
    }
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 8px;

        ${responsive &&
        `
          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
        ${containerStyle}
      `}
    >
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <CountrySelectInput
          name='country'
          value={countryValueForInput}
          onChange={handleCountryChange}
          fieldErrors={{
            country: fieldErrors?.country,
          }}
          customError={{
            country: customErrors?.country,
          }}
          label={labels?.country || 'Country'}
          placeholder={placeholders?.country || 'Select a Country'}
          disabled={disabled}
          allowClear={allowClear}
          tabIndex={countryTabIndex}
          required={requiredFields?.country}
        />
      </div>
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <ProvinceSelectInput
          name='province'
          value={provinceValueForInput}
          onChange={handleProvinceChange}
          countryId={countryId}
          fieldErrors={{
            province: fieldErrors?.province || fieldErrors?.state,
          }}
          customError={{
            province: customErrors?.province,
          }}
          label={labels?.province || 'Province/State'}
          placeholder={placeholders?.province || 'Select a Province/State'}
          disabled={disabled}
          allowClear={allowClear}
          tabIndex={provinceTabIndex}
          onProvincesLoaded={handleProvincesLoadedInternal}
          required={requiredFields?.province}
          provincesRef={provincesRef}
        />
      </div>
    </div>
  )
}

export default CountryAndProvinceInput
